// Firebase Authentication (Google sign-in). Config comes from env only.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { initializeApp, getApps } from "firebase/app";
import { getAuth, GoogleAuthProvider, RecaptchaVerifier, signInWithPhoneNumber, signInWithPopup, signOut } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebaseReady = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

const app = () => getApps()[0] || initializeApp(firebaseConfig);

export async function googleIdToken() {
  const auth = getAuth(app());
  auth.useDeviceLanguage();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const cred = await signInWithPopup(auth, provider);
  return cred.user.getIdToken();
}

export async function firebaseSignOut() {
  if (!firebaseReady || !getApps().length) return;
  try {
    await signOut(getAuth(app()));
  } catch {
    /* already signed out */
  }
}

// ---- phone OTP: used to verify the contact number on a listing ----

export async function sendPhoneOtp(e164Phone, containerId) {
  const auth = getAuth(app());
  auth.useDeviceLanguage();
  if (window.__h7Recaptcha) {
    try {
      window.__h7Recaptcha.clear();
    } catch {
      /* nothing to clear */
    }
    window.__h7Recaptcha = null;
  }
  // a rendered reCAPTCHA can't be re-rendered in the same node — always use a fresh one
  document.getElementById(containerId)?.remove();
  const host = document.createElement("div");
  host.id = containerId;
  document.body.appendChild(host);
  const verifier = new RecaptchaVerifier(auth, host, { size: "invisible" });
  await verifier.render();
  window.__h7Recaptcha = verifier;
  return signInWithPhoneNumber(auth, e164Phone, verifier);
}

export function otpErrorText(err) {
  const code = err?.code || "";
  if (code.includes("argument-error")) return "OTP could not start — check that Phone sign-in is enabled in Firebase and this domain is authorised.";
  if (code.includes("invalid-phone-number")) return "That mobile number doesn't look right.";
  if (code.includes("invalid-verification-code")) return "That OTP is incorrect. Please check and try again.";
  if (code.includes("code-expired")) return "That OTP expired — send a new one.";
  if (code.includes("too-many-requests")) return "Too many attempts. Please try again in a while.";
  if (code.includes("quota-exceeded")) return "SMS quota exhausted on the Firebase project.";
  if (code.includes("billing-not-enabled")) return "Phone sign-in needs the Firebase Blaze plan enabled.";
  if (code.includes("operation-not-allowed") || code.includes("configuration-not-found"))
    return "Phone sign-in isn't switched on yet in Firebase. Enable Authentication → Sign-in method → Phone.";
  if (code.includes("captcha-check-failed") || code.includes("unauthorized-domain"))
    return "This domain isn't authorised in Firebase yet. Add it under Authentication → Settings → Authorized domains.";
  return err?.message || "Could not send the OTP";
}

export function googleErrorText(err) {
  const code = err?.code || "";
  if (code.includes("popup-blocked")) return "Your browser blocked the Google window. Allow pop-ups and try again.";
  if (code.includes("popup-closed") || code.includes("cancelled-popup")) return "Google sign-in was cancelled.";
  if (code.includes("unauthorized-domain")) return "This domain isn't authorised in Firebase yet. Add it under Authentication → Settings → Authorized domains.";
  if (code.includes("configuration-not-found") || code.includes("operation-not-allowed"))
    return "Google sign-in isn't switched on yet in Firebase. Enable Authentication → Sign-in method → Google.";
  if (code.includes("account-exists-with-different-credential")) return "This email is already registered — log in with your password instead.";
  if (code.includes("network-request-failed")) return "Network problem reaching Google. Please try again.";
  return err?.message || "Google sign-in failed";
}
